import React, { useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { SecondaryGhostButton } from '@bibliocommons/base-buttons';
import { ChevronLink } from '@bibliocommons/base-links';
import { userIdentityShape } from '@bibliocommons/bc-prop-types';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import UserAvatar from '@bibliocommons/user-avatar';
import UserBadge from '@bibliocommons/user-badge';

import Popover from 'app/components/shared/Popover';

import './UserCardPopover.scss';

const UserCardBody = ({
  isFollowing,
  isIgnoring,
  isMobileApp,
  isSyncingFollowingState,
  isSyncingIgnoringState,
  toggleIsFollowing,
  toggleIsIgnoring,
  handleProfileLinkClick,
  userIdentity
}) => {
  const instanceIdRef = useRef(uniqueId('UserCardBody'));
  const intl = useIntl();
  const isCurrentUser = userIdentity.get('loggedIn', false);
  const hasFollowToggle = !isMobileApp;
  const hasProfileLink = !(isMobileApp || isCurrentUser);
  const hasToggleActions = !isCurrentUser;

  const isFollowToggleEnabled = hasToggleActions && hasFollowToggle && !isIgnoring && !isSyncingIgnoringState;

  const homeLibraryFullName = userIdentity.get('homeLibraryFullName');
  const name = userIdentity.get('name', '');
  const profileUrl = userIdentity.get('webUrl') || `/user_profile/${userIdentity.get('id')}`;
  const viewProfileLabelElId = `ViewProfileLink_${instanceIdRef.current}`;
  const linkText = intl.formatMessage({ id: 'view_profile' });
  return (
    <article className="user-card__body">
      <section className="user-card__left">
        <UserAvatar hideLink userIdentity={userIdentity} />
      </section>
      <section>
        <header className="user-card__header">
          <div className="user-card__title">
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
            <span className="user-card__username" tabIndex="0">
              {name}
            </span>
            <UserBadge className="user-card__user-badge" size="small" userIdentity={userIdentity} />
          </div>
          {homeLibraryFullName && (
            <>
              <span className="user-card__library">{homeLibraryFullName}</span>
              <ScreenReaderMessage>
                <FormattedMessage id="sr_library_user" values={{ libraryName: homeLibraryFullName }} />
              </ScreenReaderMessage>
            </>
          )}
        </header>
        {hasProfileLink && profileUrl && (
          <>
            <ScreenReaderMessage id={viewProfileLabelElId}>
              <FormattedMessage id="sr_view_profile" values={{ username: name }} />
            </ScreenReaderMessage>
            <ChevronLink
              aria-labelledby={viewProfileLabelElId}
              className="user-card__profile-link"
              dataKey="user-card-profile-link"
              href={profileUrl}
              size="small"
              handleClick={() => handleProfileLinkClick(linkText)}
            >
              <span className="user-card__profile-link-text">{linkText}</span>
            </ChevronLink>
          </>
        )}
        {hasToggleActions && (
          <section className="user-card__actions">
            {hasFollowToggle && (
              <SecondaryGhostButton
                aria-disabled={!isFollowToggleEnabled}
                className={cn({ 'user-card__action--following': isFollowing })}
                dataKey="toggle-is-following"
                disabled={!isFollowToggleEnabled}
                handleClick={toggleIsFollowing}
                isLoading={isSyncingFollowingState}
                size="small"
              >
                <span aria-hidden>
                  <FormattedMessage id={isFollowing ? 'unfollow' : 'follow'} />
                </span>
                <ScreenReaderMessage>
                  <FormattedMessage
                    id={isFollowing ? 'sr_unfollow_username' : 'sr_follow_username'}
                    values={{ username: name }}
                  />
                </ScreenReaderMessage>
              </SecondaryGhostButton>
            )}
            <SecondaryGhostButton
              aria-disabled={isSyncingFollowingState}
              className={cn({ 'user-card__action--ignoring': isIgnoring })}
              dataKey="toggle-is-ignoring"
              disabled={isSyncingFollowingState}
              handleClick={toggleIsIgnoring}
              isLoading={isSyncingIgnoringState}
              size="small"
            >
              <span aria-hidden>
                <FormattedMessage id={isIgnoring ? 'unignore' : 'ignore'} />
              </span>
              <ScreenReaderMessage>
                <FormattedMessage
                  id={isIgnoring ? 'sr_unignore_username' : 'sr_ignore_username'}
                  values={{ username: name }}
                />
              </ScreenReaderMessage>
            </SecondaryGhostButton>
          </section>
        )}
      </section>
    </article>
  );
};

UserCardBody.defaultProps = {
  isFollowing: false,
  isIgnoring: false,
  isMobileApp: false,
  isSyncingFollowingState: false,
  isSyncingIgnoringState: false
};

UserCardBody.propTypes = {
  isFollowing: PropTypes.bool,
  isIgnoring: PropTypes.bool,
  isMobileApp: PropTypes.bool,
  isSyncingFollowingState: PropTypes.bool,
  isSyncingIgnoringState: PropTypes.bool,
  toggleIsFollowing: PropTypes.func.isRequired,
  toggleIsIgnoring: PropTypes.func.isRequired,
  handleProfileLinkClick: PropTypes.func.isRequired,
  userIdentity: userIdentityShape.isRequired
};

export default function UserCardPopover({
  children,
  isFollowing,
  isIgnoring,
  isMobileApp,
  isSyncingFollowingState,
  isSyncingIgnoringState,
  offset,
  toggleIsFollowing,
  toggleIsIgnoring,
  handleProfileLinkClick,
  userIdentity
}) {
  const isTargetUserActive = userIdentity.get('active', false) === true;
  if (!isTargetUserActive) {
    return children;
  }

  return (
    <div className="cp-user-card">
      <Popover
        className="user-card__popover"
        dataKey="user-card-popover"
        hasTransition
        hoverDelayMilliseconds={250}
        offset={offset}
        renderContent={() => (
          <UserCardBody
            isFollowing={isFollowing}
            isIgnoring={isIgnoring}
            isMobileApp={isMobileApp}
            isSyncingFollowingState={isSyncingFollowingState}
            isSyncingIgnoringState={isSyncingIgnoringState}
            toggleIsFollowing={toggleIsFollowing}
            toggleIsIgnoring={toggleIsIgnoring}
            userIdentity={userIdentity}
            handleProfileLinkClick={handleProfileLinkClick}
          />
        )}
        triggerOnHover={!isMobileApp}
      >
        {children}
      </Popover>
    </div>
  );
}

UserCardPopover.defaultProps = {
  isFollowing: false,
  isIgnoring: false,
  isMobileApp: false,
  isSyncingFollowingState: false,
  isSyncingIgnoringState: false
};

UserCardPopover.propTypes = {
  children: PropTypes.node,
  isFollowing: PropTypes.bool,
  isIgnoring: PropTypes.bool,
  isMobileApp: PropTypes.bool,
  isSyncingFollowingState: PropTypes.bool,
  isSyncingIgnoringState: PropTypes.bool,

  offset: PropTypes.arrayOf(PropTypes.number),
  toggleIsFollowing: PropTypes.func.isRequired,
  toggleIsIgnoring: PropTypes.func.isRequired,
  handleProfileLinkClick: PropTypes.func.isRequired,
  userIdentity: userIdentityShape.isRequired
};
